import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchQueueMetrics,
  fetchQueueHistory,
  fetchDepositQueueStats,
  fetchWithdrawalQueueStats,
  fetchWebhookQueueStats,
  fetchSweepQueueStats,
  fetchSweepHistory,
  fetchFailedWebhookJobs,
  pauseQueue,
  resumeQueue,
  processQueue,
  clearFailedJobs,
  retryWebhookJob,
  retryAllWebhookJobs,
  fetchQueueJobs,
  JobType,
  promoteJob,
  deleteJob,
  changeJobPriority,
  retryJob,
  duplicateJob,
  moveJob,
  fetchFailedTransactionsStats,
  clearFailedTransactionsCache
} from '../api';

/**
 * Hook for fetching queue metrics
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with queue metrics
 */
export const useQueueMetrics = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['queueMetrics'],
    queryFn: fetchQueueMetrics,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for fetching queue metrics history
 * @param limit Maximum number of data points to return
 * @param refreshInterval Interval in milliseconds to refresh data (default: 60000)
 * @returns Query result with queue metrics history
 */
export const useQueueHistory = (limit: number = 10, refreshInterval = 60000) => {
  return useQuery({
    queryKey: ['queueHistory', limit],
    queryFn: () => fetchQueueHistory(limit),
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for fetching deposit queue statistics
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with deposit queue statistics
 */
export const useDepositQueueStats = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['depositQueueStats'],
    queryFn: fetchDepositQueueStats,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for fetching withdrawal queue statistics
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with withdrawal queue statistics
 */
export const useWithdrawalQueueStats = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['withdrawalQueueStats'],
    queryFn: fetchWithdrawalQueueStats,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for fetching webhook queue statistics
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with webhook queue statistics
 */
export const useWebhookQueueStats = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['webhookQueueStats'],
    queryFn: fetchWebhookQueueStats,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for fetching sweep queue statistics
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with sweep queue statistics
 */
export const useSweepQueueStats = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['sweepQueueStats'],
    queryFn: fetchSweepQueueStats,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for fetching sweep historical statistics from database
 * @param refreshInterval Interval in milliseconds to refresh data (default: 60000)
 * @returns Query result with sweep historical statistics
 */
export const useSweepHistory = (refreshInterval = 60000) => {
  return useQuery({
    queryKey: ['sweepHistory'],
    queryFn: fetchSweepHistory,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for fetching failed webhook jobs
 * @param limit Maximum number of jobs to return
 * @param offset Offset for pagination
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with failed webhook jobs
 */
export const useFailedWebhookJobs = (
  limit = 10,
  offset = 0,
  refreshInterval = 30000
) => {
  return useQuery({
    queryKey: ['failedWebhookJobs', limit, offset],
    queryFn: () => fetchFailedWebhookJobs(limit, offset),
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for pausing a queue
 * @returns Mutation for pausing a queue
 */
export const usePauseQueue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueName: string) => pauseQueue(queueName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['depositQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['webhookQueueStats'] });
    }
  });
};

/**
 * Hook for resuming a queue
 * @returns Mutation for resuming a queue
 */
export const useResumeQueue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueName: string) => resumeQueue(queueName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['depositQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['webhookQueueStats'] });
    }
  });
};

/**
 * Hook for processing waiting jobs in a queue
 * @returns Mutation for processing a queue
 */
export const useProcessQueue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueName: string) => processQueue(queueName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['depositQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['webhookQueueStats'] });
    }
  });
};

/**
 * Hook for clearing failed jobs in a queue
 * @returns Mutation for clearing failed jobs
 */
export const useClearFailedJobs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueName: string) => clearFailedJobs(queueName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['depositQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['withdrawalQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['webhookQueueStats'] });
      queryClient.invalidateQueries({ queryKey: ['failedWebhookJobs'] });
    }
  });
};

/**
 * Hook for retrying a failed webhook job
 * @returns Mutation for retrying a failed webhook job
 */
export const useRetryWebhookJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => retryWebhookJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['failedWebhookJobs'] });
      queryClient.invalidateQueries({ queryKey: ['webhookQueueStats'] });
    }
  });
};

/**
 * Hook for retrying all failed webhook jobs
 * @returns Mutation for retrying all failed webhook jobs
 */
export const useRetryAllWebhookJobs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: retryAllWebhookJobs,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['failedWebhookJobs'] });
      queryClient.invalidateQueries({ queryKey: ['webhookQueueStats'] });
    }
  });
};

/**
 * Hook for fetching jobs from a queue
 * @param queueName Queue name
 * @param jobType Job type
 * @param limit Maximum number of jobs to return
 * @param offset Offset for pagination
 * @param enabled Whether to enable the query
 * @returns Query result with queue jobs
 */
export const useQueueJobs = (
  queueName: string,
  jobType: JobType,
  limit: number = 10,
  offset: number = 0,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['queueJobs', queueName, jobType, limit, offset],
    queryFn: () => fetchQueueJobs(queueName, jobType, limit, offset),
    enabled: enabled && !!queueName && !!jobType
  });
};

/**
 * Hook for promoting a job to the front of the queue
 * @returns Mutation function for promoting a job
 */
export const usePromoteJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueName, jobId }: { queueName: string; jobId: string }) =>
      promoteJob(queueName, jobId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['queueJobs', variables.queueName] });
    }
  });
};

/**
 * Hook for deleting a job from the queue
 * @returns Mutation function for deleting a job
 */
export const useDeleteJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueName, jobId }: { queueName: string; jobId: string }) =>
      deleteJob(queueName, jobId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['queueJobs', variables.queueName] });
      queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
    }
  });
};

/**
 * Hook for changing job priority
 * @returns Mutation function for changing job priority
 */
export const useChangeJobPriority = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueName, jobId, priority }: { queueName: string; jobId: string; priority: number }) =>
      changeJobPriority(queueName, jobId, priority),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['queueJobs', variables.queueName] });
    }
  });
};

/**
 * Hook for retrying a failed job
 * @returns Mutation function for retrying a job
 */
export const useRetryJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueName, jobId }: { queueName: string; jobId: string }) =>
      retryJob(queueName, jobId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['queueJobs', variables.queueName] });
      queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
    }
  });
};

/**
 * Hook for duplicating a job
 * @returns Mutation function for duplicating a job
 */
export const useDuplicateJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueName, jobId }: { queueName: string; jobId: string }) =>
      duplicateJob(queueName, jobId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['queueJobs', variables.queueName] });
      queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
    }
  });
};

/**
 * Hook for moving a job to another queue
 * @returns Mutation function for moving a job
 */
export const useMoveJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceQueue, targetQueue, jobId }: { sourceQueue: string; targetQueue: string; jobId: string }) =>
      moveJob(sourceQueue, targetQueue, jobId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['queueJobs', variables.sourceQueue] });
      queryClient.invalidateQueries({ queryKey: ['queueJobs', variables.targetQueue] });
      queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
    }
  });
};

/**
 * Hook for fetching failed transactions cache statistics
 * @param refreshInterval Interval in milliseconds to refresh data (default: 0, which means no auto-refresh)
 * @returns Query result with failed transactions statistics
 */
export const useFailedTransactionsStats = <T = any>(refreshInterval = 0) => {
  return useQuery<T>({
    queryKey: ['failedTransactionsStats'],
    queryFn: fetchFailedTransactionsStats,
    refetchInterval: refreshInterval > 0 ? refreshInterval : undefined,
    staleTime: refreshInterval > 0 ? refreshInterval * 0.9 : 0
  });
};

/**
 * Hook for clearing the failed transactions cache
 * @returns Mutation for clearing the failed transactions cache
 */
export const useClearFailedTransactionsCache = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clearFailedTransactionsCache,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['failedTransactionsStats'] });
    }
  });
};
